import React from 'react';
import { UserStatus, Company } from '../../types/auth';
import { StatusBadge } from '../common/StatusBadge';

interface CompanyStatusCardProps {
    company: Company;
    onEdit?: () => void;
    onResubmit?: () => void;
    show1CStatus?: boolean;
}

export const CompanyStatusCard: React.FC<CompanyStatusCardProps> = ({
    company,
    onEdit,
    onResubmit,
    show1CStatus = false
}) => {
    const getStatusIcon = (status: UserStatus) => {
        switch (status) {
            case UserStatus.DRAFT:
                return (
                    <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                        <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                    </div>
                );
            case UserStatus.PENDING_MODERATION:
                return (
                    <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                        <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                );
            case UserStatus.NEEDS_REVISION:
                return (
                    <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                        <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                    </div>
                );
            case UserStatus.APPROVED:
                return (
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                        <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                );
            case UserStatus.VERIFIED:
                return (
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                        <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.031 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                    </div>
                );
            case UserStatus.REJECTED:
                return (
                    <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                        <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                );
            default:
                return (
                    <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                        <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                );
        }
    };

    const getStatusTitle = (status: UserStatus) => {
        switch (status) {
            case UserStatus.DRAFT:
                return 'Черновик регистрации';
            case UserStatus.PENDING_MODERATION:
                return 'Ожидает модерации';
            case UserStatus.NEEDS_REVISION:
                return 'Требует доработки';
            case UserStatus.APPROVED:
                return 'Одобрена';
            case UserStatus.VERIFIED:
                return 'Верифицирована';
            case UserStatus.REJECTED:
                return 'Отклонена';
            default:
                return 'Неизвестный статус';
        }
    };

    const getStatusDescription = (status: UserStatus) => {
        switch (status) {
            case UserStatus.DRAFT:
                return 'Завершите заполнение данных компании для подачи заявки на модерацию.';
            case UserStatus.PENDING_MODERATION:
                return 'Ваша заявка находится в очереди на рассмотрение модератором. Среднее время обработки 1-2 рабочих дня.';
            case UserStatus.NEEDS_REVISION:
                return 'Модератор запросил доработку документов. Внесите необходимые изменения и отправьте заявку повторно.';
            case UserStatus.APPROVED:
                return 'Поздравляем! Ваша компания одобрена модератором. Теперь вы можете пользоваться системой.';
            case UserStatus.VERIFIED:
                return 'Компания полностью верифицирована. Доступны все функции системы.';
            case UserStatus.REJECTED:
                return 'Заявка была отклонена. Ознакомьтесь с причинами отклонения и подайте новую заявку.';
            default:
                return '';
        }
    };

    const getProgressPercentage = (status: UserStatus) => {
        switch (status) {
            case UserStatus.DRAFT:
                return 25;
            case UserStatus.PENDING_MODERATION:
                return 50;
            case UserStatus.NEEDS_REVISION:
                return 40;
            case UserStatus.APPROVED:
                return 85;
            case UserStatus.VERIFIED:
                return 100;
            case UserStatus.REJECTED:
                return 0;
            default:
                return 0;
        }
    };

    const getProgressColor = (status: UserStatus) => {
        switch (status) {
            case UserStatus.DRAFT:
                return 'bg-gray-400';
            case UserStatus.PENDING_MODERATION:
                return 'bg-yellow-400';
            case UserStatus.NEEDS_REVISION:
                return 'bg-orange-400';
            case UserStatus.APPROVED:
                return 'bg-green-400';
            case UserStatus.VERIFIED:
                return 'bg-blue-500';
            case UserStatus.REJECTED:
                return 'bg-red-400';
            default:
                return 'bg-gray-400';
        }
    };

    const status = company.status || UserStatus.DRAFT;
    const progressPercentage = getProgressPercentage(status);
    const progressColor = getProgressColor(status);

    return (
        <div className="card">
            <div className="flex items-start justify-between mb-6">
                <div className="flex items-start space-x-4">
                    {getStatusIcon(status)}
                    <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-1">
                            {company.name}
                        </h3>
                        <p className="text-gray-600 text-sm mb-2">
                            БИН: {company.bin}
                        </p>
                        <StatusBadge status={status} />
                    </div>
                </div>

                <div className="text-right">
                    <p className="text-sm text-gray-500">Прогресс регистрации</p>
                    <p className="text-2xl font-bold text-gray-900">{progressPercentage}%</p>
                </div>
            </div>

            {/* Progress Bar */}
            <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">Прогресс регистрации</span>
                    <span className="text-sm font-bold text-gray-900">{progressPercentage}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                    <div
                        className={`h-3 rounded-full transition-all duration-700 ease-out ${progressColor} shadow-sm`}
                        style={{ width: `${progressPercentage}%` }}
                    />
                </div>
                <div className="flex justify-between mt-1 text-xs text-gray-500">
                    <span>Начало</span>
                    <span>Завершение</span>
                </div>
            </div>

            <div className="mb-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-2">
                    {getStatusTitle(status)}
                </h4>
                <p className="text-gray-600">
                    {getStatusDescription(status)}
                </p>
            </div>

            {/* Rejection Reason */}
            {status === UserStatus.REJECTED && company.rejectionReason && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <h5 className="text-sm font-medium text-red-800 mb-2">
                        Причина отклонения:
                    </h5>
                    <p className="text-sm text-red-700">
                        {company.rejectionReason}
                    </p>
                </div>
            )}

            {/* Needs Revision Reason */}
            {status === UserStatus.NEEDS_REVISION && company.rejectionReason && (
                <div className="mb-6 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                    <h5 className="text-sm font-medium text-orange-800 mb-2">
                        Комментарий модератора:
                    </h5>
                    <p className="text-sm text-orange-700">
                        {company.rejectionReason}
                    </p>
                </div>
            )}

            {/* Company Details */}
            <div className="border-t pt-6">
                <h5 className="text-sm font-medium text-gray-900 mb-3">
                    Данные компании
                </h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                        <p className="text-gray-500">Юридический адрес:</p>
                        <p className="text-gray-900">{company.legalAddress}</p>
                    </div>
                    {company.factAddress && (
                        <div>
                            <p className="text-gray-500">Фактический адрес:</p>
                            <p className="text-gray-900">{company.factAddress}</p>
                        </div>
                    )}
                    <div>
                        <p className="text-gray-500">Директор:</p>
                        <p className="text-gray-900">{company.directorName}</p>
                    </div>
                    {company.directorIin && (
                        <div>
                            <p className="text-gray-500">ИИН директора:</p>
                            <p className="text-gray-900">{company.directorIin}</p>
                        </div>
                    )}
                    {company.phoneNumber && (
                        <div>
                            <p className="text-gray-500">Телефон:</p>
                            <p className="text-gray-900">{company.phoneNumber}</p>
                        </div>
                    )}
                    {company.email && (
                        <div>
                            <p className="text-gray-500">Email:</p>
                            <p className="text-gray-900">{company.email}</p>
                        </div>
                    )}
                </div>

                {/* 1С Status and Banking Details */}
                {show1CStatus && (
                    <div className="mt-6 pt-6 border-t">
                        <h5 className="text-sm font-medium text-gray-900 mb-3">
                            Интеграция с 1С
                        </h5>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                            {/* Флаг компании из 1С */}
                            <div className="flex items-center justify-between">
                                <span className="text-gray-500">Компания из 1С:</span>
                                <div className="flex items-center">
                                    {company.from1C ? (
                                        <>
                                            <span className="text-green-600 font-medium mr-2">Да</span>
                                            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                                        </>
                                    ) : (
                                        <>
                                            <span className="text-gray-500 font-medium mr-2">Нет</span>
                                            <div className="w-3 h-3 bg-gray-300 rounded-full"></div>
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* Банковские реквизиты */}
                            {(company.bankName || company.bankBik || company.bankAccount || company.bankKbe) && (
                                <>
                                    {company.bankName && (
                                        <div>
                                            <p className="text-gray-500">Название банка:</p>
                                            <p className="text-gray-900">{company.bankName}</p>
                                        </div>
                                    )}
                                    {company.bankBik && (
                                        <div>
                                            <p className="text-gray-500">БИК:</p>
                                            <p className="text-gray-900">{company.bankBik}</p>
                                        </div>
                                    )}
                                    {company.bankAccount && (
                                        <div>
                                            <p className="text-gray-500">Номер счета:</p>
                                            <p className="text-gray-900">{company.bankAccount}</p>
                                        </div>
                                    )}
                                    {company.bankKbe && (
                                        <div>
                                            <p className="text-gray-500">КБЕ:</p>
                                            <p className="text-gray-900">{company.bankKbe}</p>
                                        </div>
                                    )}
                                </>
                            )}

                            {/* Статус банковских реквизитов */}
                            <div className="flex items-center justify-between md:col-span-2">
                                <span className="text-gray-500">Банковские реквизиты:</span>
                                <div className="flex items-center">
                                    {(company.bankName && company.bankBik && company.bankAccount) ? (
                                        <>
                                            <span className="text-green-600 font-medium mr-2">Заполнены</span>
                                            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                                        </>
                                    ) : (
                                        <>
                                            <span className="text-orange-600 font-medium mr-2">Не заполнены</span>
                                            <div className="w-3 h-3 bg-orange-400 rounded-full"></div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 mt-6 pt-6 border-t">
                {status === UserStatus.DRAFT && onEdit && (
                    <button onClick={onEdit} className="btn-primary">
                        Завершить регистрацию
                    </button>
                )}

                {status === UserStatus.NEEDS_REVISION && onEdit && (
                    <button onClick={onEdit} className="btn-primary">
                        Исправить данные
                    </button>
                )}

                {status === UserStatus.REJECTED && onResubmit && (
                    <button onClick={onResubmit} className="btn-primary">
                        Подать новую заявку
                    </button>
                )}
            </div>
        </div>
    );
};
